import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../services/language.service';
import { IProcessDetailResponse } from '../../processes/models/processes.models';

export type PolicyOrigin = 'workflow' | 'auth_preaccepted' | 'auth';

export type PolicyModalInfo = {
	uuid?: string;
	custom_id?: string | null;
	required?: boolean;
	check?: boolean;
	checked?: boolean;
	html: string | Record<string, string>;
	isExtra?: boolean;
	origin?: PolicyOrigin;
	show?: boolean;
};

export interface IUnifiedPolicyItem {
	html: string | Record<string, string>;
	htmlResolved: string;
	accepted: boolean;
	required?: boolean;
	origin: PolicyOrigin;
	modalInfo: PolicyModalInfo;
}

const POLICY_ORIGIN_I18N: Record<PolicyOrigin, string> = {
	workflow: 'PROCESSES.DETAIL.POLICY_ORIGIN_WORKFLOW',
	auth_preaccepted: 'PROCESSES.DETAIL.POLICY_ORIGIN_AUTH_PREACCEPTED',
	auth: 'PROCESSES.DETAIL.POLICY_ORIGIN_AUTH',
};

export function resolvePolicyOrigin(info: PolicyModalInfo): PolicyOrigin {
	if (info.origin) {
		return info.origin;
	}
	if (info.isExtra) {
		return info.show ? 'auth' : 'auth_preaccepted';
	}
	return 'workflow';
}

export function policyOriginLabel(origin: PolicyOrigin, trans: TranslateService): string {
	return trans.instant(POLICY_ORIGIN_I18N[origin]);
}

export function buildUnifiedPolicyItems(
	detail: IProcessDetailResponse,
	languageService: LanguageService,
): IUnifiedPolicyItem[] {
	const items: IUnifiedPolicyItem[] = [];

	for (const policyCheck of detail.data?.HOME?.policyChecks ?? []) {
		const modalInfo: PolicyModalInfo = {
			...policyCheck,
			origin: 'workflow',
		};
		items.push({
			html: policyCheck.html,
			htmlResolved: languageService.resolveLocalizedText(policyCheck.html),
			accepted: policyCheck.check,
			required: policyCheck.required,
			origin: 'workflow',
			modalInfo,
		});
	}

	const policyChecksExtra = detail.general?.policy_checks_extra ?? [];
	for (const extra of policyChecksExtra.filter((e) => !e.show)) {
		const origin: PolicyOrigin = 'auth_preaccepted';
		const modalInfo: PolicyModalInfo = {
			...extra,
			html: extra.html ?? '',
			isExtra: true,
			origin,
		};
		items.push({
			html: extra.html ?? '',
			htmlResolved: languageService.resolveLocalizedText(extra.html),
			accepted: extra.checked ?? false,
			required: extra.required,
			origin,
			modalInfo,
		});
	}

	for (const extra of policyChecksExtra.filter((e) => e.show)) {
		const origin: PolicyOrigin = 'auth';
		const modalInfo: PolicyModalInfo = {
			...extra,
			html: extra.html ?? '',
			isExtra: true,
			origin,
		};
		items.push({
			html: extra.html ?? '',
			htmlResolved: languageService.resolveLocalizedText(extra.html),
			accepted: extra.checked ?? false,
			required: extra.required,
			origin,
			modalInfo,
		});
	}

	return items;
}

export function normalizePolicyModalInfo(
	info: PolicyModalInfo,
	languageService: LanguageService,
): PolicyModalInfo & { html: string } {
	return {
		...info,
		html: languageService.resolveLocalizedText(info.html),
	};
}
